// MobileMatchRow — unified row component used by the mobile Schedule,
// Results, and Team-Detail history pages. Discriminated-union prop:
//
//   schedule     — head shows match code + time + LIVE/UP NEXT chip.
//                  Body has two alliance rows (red, blue). The team
//                  name is the navigation click target; the status pill
//                  sits beside it as a separate target (editable for
//                  ops via ProtoEditableStatusPill, otherwise read-only).
//   result       — head shows match code + time + FORFEIT/FINAL.
//                  Body has alliance rows with side scores + win/loss
//                  styling.
//   team-history — same DOM as `result` but read from the team's
//                  perspective. Shows UPCOMING for unplayed matches and
//                  pending styling on both sides.
//
// Day-marker dividers (`.mw-day-marker`) are NOT rendered here — the
// parent list inserts them between rows so it can decide grouping.

import type { Dispatch } from 'react';
import type {
  Action, AppRole, AppState, Match, MatchEvent, MatchTeamStatus, NavigateFn,
  RoundResult, TeamId,
} from '../types';
import { isForfeit } from '../types';
import { teamById } from '../mock/data';
import { ProtoEditableStatusPill, EDITABLE_STATUSES, VOL_EDITABLE_STATUSES, ProtoMatchCode, TeamLogo } from './primitives';
import { inspectionProgressFor, teamHasInspectionProgress } from '../state/inspection';
import { applyStatusEdit } from '../state/statusTransition';

type ScheduleProps = {
  kind: 'schedule';
  match: Match;
  event: MatchEvent;
  state: AppState;
  /** Optional formatted time override (e.g. delay-shifted). Falls back to match.time. */
  timeText?: string;
  /** When timeText is delay-shifted, color it amber. */
  timeDelayed?: boolean;
  onNavigate?: NavigateFn;
  /** When provided alongside `dispatch`, ops can edit the status pill
   *  (lifecycle dropdown). Without these, the pill is read-only. */
  role?: AppRole;
  dispatch?: Dispatch<Action>;
};

type ResultProps = {
  kind: 'result';
  match: Match;
  rounds: ReadonlyArray<RoundResult>;
  onNavigate?: NavigateFn;
  /** When ops, render the inline EDIT/REPLAY actions row. */
  isOps?: boolean;
  onEdit?: () => void;
  onReplay?: () => void;
};

type TeamHistoryProps = {
  kind: 'team-history';
  match: Match;
  event: MatchEvent;
  /** The team whose history is being shown — controls "their side" styling. */
  teamId: TeamId;
  rounds: ReadonlyArray<RoundResult>;
  onNavigate?: NavigateFn;
};

export type MobileMatchRowProps = ScheduleProps | ResultProps | TeamHistoryProps;

export function MobileMatchRow(props: MobileMatchRowProps) {
  if (props.kind === 'schedule') return <ScheduleRow {...props} />;
  if (props.kind === 'result')   return <ResultRow {...props} />;
  return <TeamHistoryRow {...props} />;
}

// ── Schedule ──────────────────────────────────────────────────────────

function ScheduleRow({ match, event, state, timeText, timeDelayed, onNavigate, role, dispatch }: ScheduleProps) {
  const r = teamById(match.red);
  const b = teamById(match.blue);
  const onTeam = (id: TeamId) => onNavigate && onNavigate('teams', { team: id });
  // Volunteer + staff get the narrowed (not_tracked ↔ at_inspection)
  // dropdown for pre-queuing teams; ops keeps the full editable set
  // for ad-hoc fixes. Same SET_MATCH_STATUS path Match Control uses →
  // both surfaces update via the reducer. Volunteers (only) are
  // blocked from reverting at_inspection → not_tracked once any robot
  // for that (team, match) has cleared S1; staff + ops can override.
  const isOps = role === 'ops';
  const isVolPlus = role === 'volunteer' || role === 'staff' || isOps;
  const editableStatusesForRole = isOps ? EDITABLE_STATUSES : VOL_EDITABLE_STATUSES;
  const editStatus = (side: 'red' | 'blue') => (status: MatchTeamStatus) => {
    if (dispatch) applyStatusEdit(dispatch, match, side, status, event, isOps);
  };
  const canVolEditSide = (teamId: string, currentStatus: MatchTeamStatus): boolean => {
    if (!isVolPlus) return false;
    if (role !== 'volunteer') return true;
    if (currentStatus !== 'at_inspection') return true;
    return !teamHasInspectionProgress(teamId, match.id, state);
  };
  // Swiss bye (1v1 quals, odd roster): the byed team sits this round out. Show it
  // in the schedule (so the team isn't left wondering) — no opponent or pills.
  if (match.bye) {
    return (
      <div className="mw-mrow">
        <div className="mw-mrow-head">
          <div className="row gap-2 min-w-0">
            <ProtoMatchCode code={match.id} />
            <span className="mono tnum txt-11 text-1">{timeText ?? match.time}</span>
          </div>
          <span className="mono txt-9 text-2 tracking-5 fw-bold">BYE</span>
        </div>
        <div className="mw-mrow-body">
          <div className="mw-mrow-side">
            <div className="swatch red" />
            <span
              className={`name ${onNavigate ? 'team-name-link' : ''}`}
              onClick={onNavigate ? () => onTeam(match.red) : undefined}
              title={onNavigate ? `Open ${r.name}` : undefined}
            >{r.short}</span>
            <span className="mono txt-9 text-3 tracking-2">sits out this round</span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`mw-mrow ${match.live ? 'live' : ''}`}>
      <div className="mw-mrow-head">
        <div className="row gap-2 min-w-0">
          <ProtoMatchCode code={match.id} live={match.live} onDeck={match.onDeck} />
          <span
            className={`mono tnum txt-11 ${timeDelayed ? 'text-warn' : 'text-1'}`}
          >
            {timeText ?? match.time}
          </span>
        </div>
        {match.live && (
          <span className="mono txt-9 text-accent tracking-5 fw-bold">● LIVE</span>
        )}
        {!match.live && match.onDeck && (
          <span className="mono txt-9 text-2 tracking-5">UP NEXT</span>
        )}
      </div>
      <div className="mw-mrow-body">
        {/* Click target is the team name only — the status pill is a
          *   separate element so tapping it doesn't navigate (and the
          *   ops edit dropdown takes over instead). */}
        <div className="mw-mrow-side">
          <div className="swatch red" />
          <span
            className={`name ${onNavigate ? 'team-name-link' : ''}`}
            onClick={onNavigate ? () => onTeam(match.red) : undefined}
            title={onNavigate ? `Open ${r.name}` : undefined}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          ><TeamLogo id={match.red} size={15} />{r.short}</span>
          <ProtoEditableStatusPill
            status={match.redS}
            onChange={canVolEditSide(match.red, match.redS) ? editStatus('red') : undefined}
            editableStatuses={editableStatusesForRole}
            inspection={inspectionProgressFor(match.red, match.id, event, state)}
          />
        </div>
        <div className="mw-mrow-side">
          <div className="swatch blue" />
          <span
            className={`name ${onNavigate ? 'team-name-link' : ''}`}
            onClick={onNavigate ? () => onTeam(match.blue) : undefined}
            title={onNavigate ? `Open ${b.name}` : undefined}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          ><TeamLogo id={match.blue} size={15} />{b.short}</span>
          <ProtoEditableStatusPill
            status={match.blueS}
            onChange={canVolEditSide(match.blue, match.blueS) ? editStatus('blue') : undefined}
            editableStatuses={editableStatusesForRole}
            inspection={inspectionProgressFor(match.blue, match.id, event, state)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Result ────────────────────────────────────────────────────────────

function ResultRow({ match, rounds, onNavigate, isOps, onEdit, onReplay }: ResultProps) {
  const r = teamById(match.red);
  const b = teamById(match.blue);
  const onTeam = (id: TeamId) => onNavigate && onNavigate('teams', { team: id });

  // Swiss bye (1v1 quals, odd roster): one team, no opponent, auto-win.
  if (match.bye) {
    return (
      <div className="mw-mrow">
        <div className="mw-mrow-head">
          <div className="row gap-2 min-w-0">
            <ProtoMatchCode code={match.id} />
            <span className="mono tnum txt-11 text-1">{match.time}</span>
          </div>
          <span className="mono txt-9 text-2 tracking-5 fw-bold">BYE</span>
        </div>
        <div className="mw-mrow-body">
          <div className="mw-mrow-side mw-result win">
            <div className="swatch red" />
            <span
              className={`name ${onNavigate ? 'team-name-link' : ''}`}
              onClick={onNavigate ? () => onTeam(match.red) : undefined}
              title={onNavigate ? `Open ${r.name}` : undefined}
            >{r.short}</span>
            <span className="mw-result-score">BYE</span>
          </div>
        </div>
      </div>
    );
  }

  const redForfeit  = isForfeit(match.redS);
  const blueForfeit = isForfeit(match.blueS);
  const redWins  = rounds.filter((rd) => rd.winner === 'red').length;
  const blueWins = rounds.filter((rd) => rd.winner === 'blue').length;

  const winnerSide: 'red' | 'blue' | 'tie' =
    redForfeit && !blueForfeit  ? 'blue' :
    blueForfeit && !redForfeit  ? 'red'  :
    redWins  > blueWins         ? 'red'  :
    blueWins > redWins          ? 'blue' : 'tie';

  // Pick which side gets `win` styling. For ties, neither wins (both get
  // tie styling).
  const sideClass = (side: 'red' | 'blue') => {
    if (winnerSide === 'tie') return 'tie';
    return winnerSide === side ? 'win' : 'loss';
  };

  // Score column: per-side round-win count. Spectators read at-a-glance.
  // Forfeits show "FF" on the forfeiting side and a 2-0 sweep for the
  // winner (matches the desktop "2—0" and how standings score it).
  const scoreFor = (side: 'red' | 'blue'): string => {
    if (side === 'red'  && redForfeit)  return 'FF';
    if (side === 'blue' && blueForfeit) return 'FF';
    if (redForfeit || blueForfeit) return String(side === winnerSide ? 2 : 0);
    if (rounds.length === 0) return '–';
    return String(side === 'red' ? redWins : blueWins);
  };

  const headLabel = redForfeit || blueForfeit
    ? `FORFEIT · ${redForfeit ? 'RED' : 'BLUE'}`
    : 'FINAL';

  return (
    <div className="mw-mrow">
      <div className="mw-mrow-head">
        <div className="row gap-2 min-w-0">
          <ProtoMatchCode code={match.id} />
          <span className="mono tnum txt-11 text-1">{match.time}</span>
        </div>
        <span
          className={`mono txt-9 tracking-5 fw-bold ${redForfeit || blueForfeit ? 'text-err' : 'text-2'}`}
        >
          {headLabel}
        </span>
      </div>
      <div className="mw-mrow-body">
        {/* Click target is the team name only — the score sits beside it
          *   as a non-interactive readout. */}
        <div className={`mw-mrow-side mw-result ${sideClass('red')}`}>
          <div className="swatch red" />
          <span
            className={`name ${onNavigate ? 'team-name-link' : ''}`}
            onClick={onNavigate ? () => onTeam(match.red) : undefined}
            title={onNavigate ? `Open ${r.name}` : undefined}
          >{r.short}</span>
          <span className="mw-result-score">{scoreFor('red')}</span>
        </div>
        <div className={`mw-mrow-side mw-result ${sideClass('blue')}`}>
          <div className="swatch blue" />
          <span
            className={`name ${onNavigate ? 'team-name-link' : ''}`}
            onClick={onNavigate ? () => onTeam(match.blue) : undefined}
            title={onNavigate ? `Open ${b.name}` : undefined}
          >{b.short}</span>
          <span className="mw-result-score">{scoreFor('blue')}</span>
        </div>
      </div>
      {isOps && (
        <div className="mw-mrow-actions">
          <button
            className="mw-inline-btn"
            onClick={onEdit}
            title="Edit recorded result"
          >EDIT</button>
          <button
            className="mw-inline-btn warn"
            onClick={onReplay}
            title="Reset and re-run this match"
          >REPLAY ⟳</button>
        </div>
      )}
    </div>
  );
}

// ── Team history ──────────────────────────────────────────────────────

function TeamHistoryRow({ match, teamId, rounds, onNavigate }: TeamHistoryProps) {
  const isRed = match.red === teamId;
  const teamShort = teamById(teamId).short;

  // Swiss bye: this team sat the round out (auto-win, no opponent).
  if (match.bye) {
    return (
      <div className="mw-mrow">
        <div className="mw-mrow-head">
          <div className="row gap-2 min-w-0">
            <ProtoMatchCode code={match.id} />
            <span className="mono tnum txt-11 text-1">{match.time}</span>
          </div>
          <span className="mono txt-9 text-2 tracking-5 fw-bold">BYE</span>
        </div>
        <div className="mw-mrow-body">
          <div className="mw-mrow-side mw-result win">
            <div className="swatch red" />
            <span className="name">{teamShort}</span>
            <span className="mw-result-score">BYE</span>
          </div>
        </div>
      </div>
    );
  }
  const oppId = isRed ? match.blue : match.red;
  const opp = teamById(oppId);
  const myStatus = isRed ? match.redS : match.blueS;
  const oppStatus = isRed ? match.blueS : match.redS;

  // Concluded statuses (recorded in the past) → result-style row.
  // Anything else (not_tracked, l2/l3, in_game, at_inspection…) → upcoming.
  const concludedStatuses = ['completed', 'forfeit_v', 'forfeit_iv'] as const;
  const upcoming = !concludedStatuses.includes(myStatus as typeof concludedStatuses[number])
                   || !concludedStatuses.includes(oppStatus as typeof concludedStatuses[number]);

  const myWins  = rounds.filter((r) => r.winner !== 'tie' && (r.winner === 'red') === isRed).length;
  const oppWins = rounds.filter((r) => r.winner !== 'tie' && (r.winner === 'red') !== isRed).length;
  const ties    = rounds.filter((r) => r.winner === 'tie').length;

  // A forfeit records no rounds — score it as a 2-0 sweep for the winner.
  const myForfeit  = isForfeit(myStatus);
  const oppForfeit = isForfeit(oppStatus);
  const matchForfeited = myForfeit || oppForfeit;

  // Build sides in [red, blue] order so layout matches every other match row.
  const teamWins     = matchForfeited ? (oppForfeit && !myForfeit ? 2 : 0) : myWins;
  const opponentWins = matchForfeited ? (myForfeit && !oppForfeit ? 2 : 0) : oppWins;
  let teamSideClass: 'win' | 'loss' | 'tie' | 'pending';
  let oppSideClass:  'win' | 'loss' | 'tie' | 'pending';
  if (upcoming) {
    teamSideClass = 'pending';
    oppSideClass  = 'pending';
  } else if (matchForfeited) {
    if (oppForfeit && !myForfeit)      { teamSideClass = 'win';  oppSideClass = 'loss'; }
    else if (myForfeit && !oppForfeit) { teamSideClass = 'loss'; oppSideClass = 'win';  }
    else                               { teamSideClass = 'tie';  oppSideClass = 'tie';  }
  } else if (rounds.length === 0) {
    teamSideClass = 'pending';
    oppSideClass  = 'pending';
  } else if (myWins > oppWins) {
    teamSideClass = 'win';  oppSideClass = 'loss';
  } else if (myWins < oppWins) {
    teamSideClass = 'loss'; oppSideClass = 'win';
  } else {
    teamSideClass = 'tie';  oppSideClass = 'tie';
  }

  const sides = isRed
    ? [
        { which: 'red'  as const, name: teamShort,  score: upcoming ? '–' : teamWins,     cls: teamSideClass, id: teamId },
        { which: 'blue' as const, name: opp.short,  score: upcoming ? '–' : opponentWins, cls: oppSideClass,  id: oppId },
      ]
    : [
        { which: 'red'  as const, name: opp.short,  score: upcoming ? '–' : opponentWins, cls: oppSideClass,  id: oppId },
        { which: 'blue' as const, name: teamShort,  score: upcoming ? '–' : teamWins,     cls: teamSideClass, id: teamId },
      ];

  const onTeam = (id: TeamId) => {
    if (id === teamId) return;          // already viewing this team
    if (onNavigate) onNavigate('teams', { team: id });
  };

  const headLabel = upcoming
    ? 'UPCOMING'
    : myForfeit
      ? 'FORFEIT · YOU'
      : oppForfeit
        ? 'FORFEIT · OPP'
        : ties > 0 && teamSideClass === 'tie'
          ? 'TIE'
          : 'FINAL';

  return (
    <div className="mw-mrow">
      <div className="mw-mrow-head">
        <div className="row gap-2 min-w-0">
          <ProtoMatchCode code={match.id} live={match.live} onDeck={match.onDeck} />
          <span className="mono tnum txt-11 text-1">{match.time}</span>
        </div>
        <span className="mono txt-9 text-2 tracking-5 fw-bold">
          {headLabel}
        </span>
      </div>
      <div className="mw-mrow-body">
        {sides.map((s) => {
          // Don't make the *current* team's name a link — they're already
          // viewing it. Only the opponent gets the navigation affordance.
          const linkable = onNavigate && s.id !== teamId;
          return (
            <div
              key={s.which}
              className={`mw-mrow-side mw-result ${s.cls}`}
            >
              <div className={`swatch ${s.which}`} />
              <span
                className={`name ${linkable ? 'team-name-link' : ''}`}
                onClick={linkable ? () => onTeam(s.id) : undefined}
                title={linkable ? `Open ${s.name}` : undefined}
              >{s.name}</span>
              <span className="mw-result-score">{s.score}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
